import { useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import classNames from 'classnames';

import { useGetSuggestedDoctorList } from '@api/ocs/doctor/index.rq';
import { Button } from '@com/_atoms/NewButton';
import VerticalDoctorCard from '@com/shared/cards/doctor/vertical-doctor-card';
import VerticalDoctorShimmer from '@com/shared/cards/doctor/vertical-doctor-shimmer';
import { colorPallete } from '@configs/color-pallete';
import { colors } from '@configs/Theme';
import { CarouselTexts } from '@constant/ocs/cards';
import { similarDoctorsText } from '@constant/ocs/doctor';
import useInView from '@hooks/useInView';
import Icon from '@utilities/icon';
import { routeList } from '@routes/routeList';

const ScrollSlider = dynamic(() => import('@com/shared/ocs/scroll-slider'));

interface Props {
  doctorId: number;
  specialityId: number;
  backgroundColor?: string;
  headerBackgroundColor?: string;
  carouselCardClassName?: string;
  containerClassName?: string;
  scrollSliderClassName?: string;
  isShowMoreButton?: boolean;
}

const shimerData = [...Array(10).keys()];

const SuggestedDoctorsCarousel = ({
  doctorId,
  specialityId,
  backgroundColor,
  carouselCardClassName,
  containerClassName,
  scrollSliderClassName,
  isShowMoreButton = true,
}: Props) => {
  const { ref, isInView } = useInView();

  const { data, isLoading } = useGetSuggestedDoctorList(
    { doctorId: String(doctorId), specialityId: String(specialityId) },
    { enabled: !!doctorId && isInView },
  );

  const { push } = useRouter();

  const [routeLoadingId, setRouteLoadingId] = useState<number | null>(null);

  const onClickHandler = (recId: number) => {
    if (!recId) return;
    setRouteLoadingId(recId);
    push(`${routeList?.doctorProfile}/${recId}`);
  };

  return (
    <div
      ref={ref}
      className={classNames(containerClassName, 'flex flex-col')}
      style={
        backgroundColor
          ? { backgroundColor: backgroundColor ? backgroundColor : '' }
          : {}
      }
    >
      <div
        className={classNames(
          'h-[48px] flex items-center justify-between px-4',
        )}
      >
        <div className="flex items-center ">
          <span
            className={classNames(
              'text-Label/Medium px-2 text-content-primary',
            )}
          >
            {similarDoctorsText.title}
          </span>
        </div>

        {isShowMoreButton && (
          <Button
            variant="secondary"
            size="medium"
            className="h-10 w-10 rounded-full !p-0 "
            onClick={() => push(`${routeList?.ocsDoctorsList}/${specialityId}`)}
            icon={
              <Icon
                name="ArrowLeft"
                width={1.5}
                height={1.5}
                fill={colorPallete.content.primary}
              />
            }
          />
        )}
      </div>

      <ScrollSlider
        className={classNames(
          !isShowMoreButton && 'pl-4',
          'flex h-full items-center gap-x-2 px-4',
          scrollSliderClassName,
        )}
      >
        <>
          {isLoading ? (
            shimerData.map((item) => (
              <VerticalDoctorShimmer
                key={item}
                className="w-[152px] h-[204px]"
              />
            ))
          ) : (
            <>
              {data?.result?.map((item) => {
                return (
                  <VerticalDoctorCard
                    onClick={() => onClickHandler(item?.recId)}
                    isLoading={
                      !!routeLoadingId && item?.recId === routeLoadingId
                    }
                    className={carouselCardClassName}
                    imageUrl={item?.imageUrl}
                    title={item?.fullName}
                    subtitle={item?.latestSpecialityName}
                    star={item?.rate}
                    key={item?.recId}
                    isOndemand
                    isScheduled
                  />
                );
              })}
            </>
          )}
        </>

        {isShowMoreButton && (
          <div
            onClick={() => push(`${routeList?.ocsDoctorsList}/${specialityId}`)}
            className="min-w-[152px] h-[212px] flex flex-col items-center justify-center cursor-pointer text-content-primary text-Label/xSmall bg-surface-primary rounded-lg shadow-fab"
            onKeyDown={() => {
              return;
            }}
            tabIndex={0}
            role="button"
          >
            <Icon
              name="ArrowLeft"
              width={1.25}
              height={1.25}
              fill={colors?.black}
            />

            {CarouselTexts?.viewAll}
          </div>
        )}
      </ScrollSlider>
    </div>
  );
};
export default SuggestedDoctorsCarousel;
